import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Integration, License } from 'lib/types';
import { RootState } from 'reducers';

import 'styles.scss';
import LicenseEditor from 'components/LicenseEditor/LicenseEditor';
import { Status } from 'reducers/enum';
import {
    removeError,
    retrieveLicenseThunk,
} from 'reducers/integrations/actions';
import LoaderOverlay from 'components/LoaderOverlay/LoaderOverlay';
import Button from 'components/Button/Button';

const getDefaultLicense: () => License = () => ({
    softwareLicenseCode: '',
    licenseType: '',
});

function IntegrationEditPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { integrationId, licenseId } = useParams();

    const retrieveLicenseStatus = useSelector(
        (state: RootState) => state.integrations.retrieveLicenseStatus
    );

    const integration: Integration = useSelector(
        (state: RootState) => state.integrations.byId[integrationId]
    );

    const license: License = integration?.licenses?.find((license) => {
        return license.id === licenseId;
    });

    const isNewLicense = license == null;
    const isLoading = retrieveLicenseStatus === Status.Pending;
    const licenseTitle =
        license != null
            ? 'Edit License Registration'
            : 'Add License Registration';

    // If an integration with the given ID does not exist, go back to integrations
    useEffect(() => {
        if (integration == null) {
            navigate('/integrations');
        }
    }, [integration, navigate]);

    // If the license does not exist but a license ID is provided, try to retrieve the license first
    useEffect(() => {
        if (isNewLicense && licenseId) {
            dispatch(retrieveLicenseThunk({ integrationId, licenseId }));
        }
    }, [dispatch, integrationId, isNewLicense, licenseId]);

    // If we've tried to retrieve the license with the provided ID
    // and the license still does not exist, redirect to creating a new license
    useEffect(() => {
        if (
            licenseId &&
            isNewLicense &&
            retrieveLicenseStatus === Status.Finished
        ) {
            navigate(`/integrations/${integrationId}/licenses/create-license`, {
                replace: true,
            });
        }
    }, [
        integrationId,
        isNewLicense,
        licenseId,
        navigate,
        retrieveLicenseStatus,
    ]);

    const handleBackClicked = useCallback(() => {
        navigate(`/integrations/${integrationId}/licenses`);
    }, [integrationId, navigate]);

    return (
        <>
            <LoaderOverlay isLoading={isLoading} />
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex flex-row justify-content-between align-items-start">
                            <h1 className="msds-text-header-1 msds-text-clear-blue mb-10">
                                {licenseTitle}
                            </h1>
                            <Button
                                color="secondary"
                                className="mb-4 mt-0"
                                type="button"
                                onClick={handleBackClicked}
                            >
                                Back to licenses
                            </Button>
                        </div>
                        {!isLoading && (
                            <LicenseEditor
                                license={license || getDefaultLicense()}
                                integration={integration}
                                isNewLicense={isNewLicense}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default IntegrationEditPage;
